import { createContext, useContext, useEffect, useId, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createSchemaGraph, serializeSchema } from '@/lib/schema-graph.mjs';

type Entry = { route: string; title?: string; description?: string; node?: Record<string, any> };
const SchemaContext = createContext<((id: string, entry: Entry | null) => void) | null>(null);

export function SchemaGraphProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const register = useMemo(() => (id: string, entry: Entry | null) => setEntries(current => {
    const next = { ...current };
    if (entry) next[id] = entry; else delete next[id];
    return next;
  }), []);
  const active = Object.values(entries).filter(entry => entry.route === pathname);
  const page = active.find(entry => entry.title);
  const payload = serializeSchema(createSchemaGraph({ route: pathname, title: page?.title || 'Linkzy', description: page?.description || '', nodes: active.flatMap(entry => entry.node ? [entry.node] : []) }));

  useEffect(() => {
    // Replace build-time graph and own one script across both Helmet implementations.
    document.head.querySelectorAll('script[data-linkzy-schema]').forEach(script => script.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.linkzySchema = 'graph';
    script.textContent = payload;
    document.head.appendChild(script);
    return () => script.remove();
  }, [payload]);
  return <SchemaContext.Provider value={register}>{children}</SchemaContext.Provider>;
}

export function useSchemaEntry(entry: Omit<Entry, 'route'>) {
  const register = useContext(SchemaContext);
  const { pathname } = useLocation();
  const id = useId();
  const payload = JSON.stringify(entry);
  useEffect(() => {
    register?.(id, { ...JSON.parse(payload), route: pathname });
    return () => register?.(id, null);
  }, [register, id, pathname, payload]);
}